import type { LinariaClassName } from '@linaria/core'
import { cx } from '@linaria/core'
import type { Message } from '../../../../../types/state'
import {
    calculateNewsSettings,
    showNewsFromFriends,
    showNewsInClubShows,
    showNewsInPrivateShows,
    showsNewsInRooms,
} from '../../../../mfcLogic/api/news'
import { getRoomsState } from '../../../../state/getState'
import { aspectRatio, getPercentage } from '../../../../utilities/dimensions'
import {
    COMPONENT_HEIGHTS,
    MIN_CHAT_WIDTH,
    MIN_VIDEO_WIDTH,
} from '../../../theme'

export const resolveTestId = (message: Message) => {
    const {
        adminAnnouncement,
        roomHelper,
        countdown,
        chatCard,
        warning,
        sarcasm,
        system,
        error,
        tip,
        pm,
        me,
    } = message
    if (chatCard) return `Chat__MessageChatCard`
    if (warning) return `Chat__MessageWarning`
    if (sarcasm) return `Chat__MessageSarcasm`
    if (system && tip) return `Chat__MessageTip`
    if (system && adminAnnouncement) return `Chat__AdminAnnouncement`
    if (roomHelper) return `Chat__MessageRoomHelper`
    if (countdown) return `Chat__MessageCountdown`
    if (system) return `Chat__MessageSystem`
    if (error) return `Chat__MessageError`
    if (pm) return `Chat__MessagePM`
    if (me) return `Chat__MessageMyself`
    return `Chat__MessageDefault`
}

export const isMessagePM = (message: Message) => {
    const {
        adminAnnouncement,
        confirmation,
        clubShow,
        warning,
        system,
        error,
        tip,
        pm,
        me,
    } = message
    if (confirmation) return
    if (tip) return
    if (clubShow) return
    if (error) return
    if (warning) return
    if (adminAnnouncement) return
    if (system) return
    if (pm) return true
    if (me) return true
}

export type ResolveStyleStyles = {
    me?: LinariaClassName
    they?: LinariaClassName
    emote?: LinariaClassName
    sarcasm?: LinariaClassName
    notification?: LinariaClassName
    warning?: LinariaClassName
    error?: LinariaClassName
    system?: LinariaClassName
    countdown?: LinariaClassName
    roomHelper?: LinariaClassName
    adminAnnouncement?: LinariaClassName
    clubShow?: LinariaClassName
    tip?: LinariaClassName
    shareTip?: LinariaClassName
    confirmation?: LinariaClassName
    message?: LinariaClassName
}

export const resolveStyle = (message: Message, styles: ResolveStyleStyles) => {
    const {
        adminAnnouncement,
        roomHelper,
        countdown,
        clubShow,
        warning,
        sarcasm,
        system,
        emote,
        error,
        tip,
        pm,
        me,
        confirmation,
    } = message

    if (emote) {
        if (!pm) return cx(styles.emote)
        if (me) return cx(styles.me, styles.emote)
        return cx(styles.they, styles.emote)
    }
    if (sarcasm) {
        if (!pm) return cx(styles.sarcasm)
        if (me) return cx(styles.me, styles.sarcasm)
        return cx(styles.they, styles.sarcasm)
    }
    if (confirmation) return cx(styles.confirmation)
    if (tip) return cx(styles.tip)
    if (clubShow) return cx(styles.clubShow)
    if (error) return cx(styles.error)
    if (warning) return cx(styles.warning)
    if (adminAnnouncement) return cx(styles.adminAnnouncement)
    if (countdown) return cx(styles.countdown)
    if (roomHelper) return cx(styles.roomHelper)
    if (system) return cx(styles.system)
    if (me) return cx(styles.me)
    if (pm) return cx(styles.they)
    return cx(styles.message)
}

/** ensure video is at least 320 px wide */
export const minVideoPercentage = (width: number) =>
    getPercentage(MIN_VIDEO_WIDTH, width)

/** ensure chat is at least 250 px wide */
export const maxVideoPercentage = (width: number) =>
    width ? Math.floor(100 - (MIN_CHAT_WIDTH * 100) / width) : 100

/** results in chat ~300-400 px wide */
export const defaultVideoPercentage = (width: number) =>
    width
        ? width < 960
            ? 60
            : width < 1440
              ? 70
              : Math.floor(100 - (400 * 100) / width)
        : 100

export const getDivision = (x: number, width: number) => {
    const percentage = getPercentage(x, width)
    const minimum = minVideoPercentage(width)
    const maximum = maxVideoPercentage(width)
    return Math.max(Math.min(percentage, maximum), minimum)
}

export const getDefaultDivision = (width: number, height: number) => {
    const defaultDivision = defaultVideoPercentage(width)
    const maxVideoWidth = (width * defaultDivision) / 100
    // const { width: nextVideoWidth = 0 } = calcVideoDimensions(
    //     maxVideoWidth,
    //     height,
    // )
    const { actionsBar, chatHeader } = COMPONENT_HEIGHTS
    const maxAvailableHeight = height - chatHeader - actionsBar
    const { width: nextVideoWidth = 0 } = aspectRatio({
        height: maxAvailableHeight,
        width: maxVideoWidth,
    })
    // return { width, height }
    const nextDivision = getPercentage(nextVideoWidth, width)
    return nextDivision
}

export const filterNewsMessages = (
    messages: Array<Message> = [],
    news: number,
    streamType: string,
    friendsListIds: number[],
) => {
    const newsSettings = calculateNewsSettings(news)
    const showNews = showsNewsInRooms(newsSettings.FCNEWSOPT_IN_CHAN)
    const showNewsInClubs = showNewsInClubShows(
        newsSettings.FCNEWSOPT_IN_CHAN_NOGRP,
    )
    const showNewsInPrivates = showNewsInPrivateShows(
        newsSettings.FCNEWSOPT_IN_CHAN_NOPVT,
    )
    const showFriendsNews = showNewsFromFriends(
        newsSettings.FCNEWSOPT_ADDFRIENDS_OFF,
    )
    const noNews =
        showNews === false ||
        (streamType === 'club' && showNewsInClubs === false) ||
        (streamType === 'group' && showNewsInClubs === false) ||
        (streamType === 'voyeur' && showNewsInPrivates === false) ||
        (streamType === 'private' && showNewsInPrivates === false)
    const friendsListIdsSet = new Set(friendsListIds)

    return messages.filter((msg) => {
        if (!msg) {
            const state = getRoomsState()
            console.error(
                '[filterNewsMessages] No message found, this should not be happening.',
                JSON.stringify({ state, messages }, null, 4),
            )
            return false
        }

        if (!msg.news) return true
        if (noNews) return false
        if (showFriendsNews === false) {
            return !friendsListIdsSet.has(msg?.user?.id)
        }
        return true
    })
}

// export const filterInlinePMs = (
//     messages: Array<Message> = [],
//     showInlinePM: boolean,
// ) => {
//     return messages.filter(
//         ({ pm, pmCommand }) => !pm || showInlinePM || pmCommand,
//     )
// }
// export const filterOnlyTips = (
//     messages: Array<Message> = [],
//     onlyTips: boolean,
// ) => {
//     return messages.filter(({ tip }) => !onlyTips || tip)
// }

export const filterMessages = (
    messages: Array<Message> = [],
    news: number,
    streamType: string,
    // showInlinePM: boolean,
    friendsListIds: Array<number>,
    // onlyTips = false,
) => {
    // const tipFilteredMessages = filterOnlyTips(messages, onlyTips)
    const noNews = filterNewsMessages(
        messages,
        news,
        streamType,
        friendsListIds,
    )
    return noNews //filterInlinePMs(noNews, showInlinePM)
}
